/**
 * Cookie Banner Component
 *
 * Simplified cookie banner that uses PostHog directly for consent management.
 * Based on: https://posthog.com/tutorials/react-cookie-banner
 *
 * Features:
 * - Works with PostHog's cookieless_mode: 'on_reject' configuration
 * - Falls back to localStorage if PostHog is blocked by adblocker
 * - Only shown in production (where PostHog is active)
 * - Privacy-focused: respects child profile protections
 *
 * Note: Parent component controls visibility via useCookieConsent hook.
 * This component only handles user interaction.
 */

import { Anchor, Button, Group, Paper, Stack, Text } from '@mantine/core'
import { Link } from '@tanstack/react-router'
import { CONSENT_STORAGE_KEY } from '@/types'
import { updateSessionRecordingState, usePostHog } from '@/lib/posthog'

interface CookiesBannerProps {
  onClose: () => void
}

export function CookiesBanner({ onClose }: CookiesBannerProps) {
  const posthog = usePostHog()

  const handleAcceptAll = () => {
    // Always set localStorage (works even if PostHog is blocked by adblocker or still loading)
    localStorage.setItem(CONSENT_STORAGE_KEY, 'granted')

    // Then try PostHog if available (wrapped in try-catch for adblocker safety)
    if (posthog) {
      try {
        posthog.opt_in_capturing()
        // Enable session recording when consent is granted
        // Note: Profile is not available yet (user might not be signed in)
        // The PostHogSessionRecordingUpdater in authed layout will handle profile-based updates
        updateSessionRecordingState(posthog, true)
      } catch {
        // PostHog may be blocked by adblocker - localStorage fallback already set
      }
    }

    onClose()
  }

  const handleRejectAll = () => {
    // Always set localStorage (works even if PostHog is blocked by adblocker or still loading)
    localStorage.setItem(CONSENT_STORAGE_KEY, 'denied')

    // Then try PostHog if available (wrapped in try-catch for adblocker safety)
    if (posthog) {
      try {
        posthog.opt_out_capturing()
        // Disable session recording when consent is rejected
        updateSessionRecordingState(posthog, false)
      } catch {
        // PostHog may be blocked by adblocker - localStorage fallback already set
      }
    }

    onClose()
  }

  return (
    <Paper
      withBorder
      p="lg"
      radius="md"
      shadow="md"
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        maxWidth: 500,
        zIndex: 1000,
      }}
    >
      <Stack gap="md">
        <Text fz="md" fw={500}>
          We value your privacy
        </Text>

        <Text c="dimmed" fz="xs">
          We use cookies to improve your experience and understand how you use our app.
          All tracking is automatically disabled for child profiles (under 18). Learn more in our{' '}
          <Anchor component={Link} to="/cookie-policy" size="xs" fw={600}>
            Cookie Policy
          </Anchor>
          {' '}and{' '}
          <Anchor component={Link} to="/privacy" size="xs" fw={600}>
            Privacy Policy
          </Anchor>
          .
        </Text>

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" size="xs" onClick={handleRejectAll}>
            Reject All
          </Button>
          <Button variant="filled" size="xs" onClick={handleAcceptAll}>
            Accept All
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}
